/**
 * RESPONSIBILITY: a picture that opens full size when it is clicked.
 *
 * For pictures that are read rather than glanced at — a printed timeline, a QR code to be
 * scanned from a phone held up to the screen. Shown in place at the size the layout
 * gives it; a click opens it as large as the window allows, over a dimmed page.
 *
 * A native <dialog>, so Escape closes it, focus is held inside it while open and handed
 * back to the picture afterwards, and a screen reader is told a dialog opened — none of
 * which a hand-built overlay gets right without a library. A tap anywhere — on the
 * picture or beside it — closes it too, because on a phone there is no Escape key.
 *
 * With no picture uploaded it is the ordinary placeholder: there is nothing to enlarge.
 */
import { useRef } from 'react';
import { hasImage, type ImageValue } from '@framework/core';
import { Picture } from './Picture';

export function Enlarge({ value, className = '' }: { value: ImageValue | undefined; className?: string }) {
  const dialog = useRef<HTMLDialogElement>(null);
  if (!hasImage(value)) return <Picture value={value} className={className} />;

  return (
    <>
      <button
        type="button" className={`enlarge ${className}`}
        aria-haspopup="dialog" onClick={() => dialog.current?.showModal()}
      >
        <Picture value={value} />
        <span className="enlarge-mark" aria-hidden="true">⤢</span>
      </button>
      <dialog
        ref={dialog} className="enlarge-dialog" aria-label={value.alt || undefined}
        // The dialog element IS the backdrop's hit area; a click on the picture lands on
        // the <img>, so only a click beside it reaches here as the target.
        onClick={(e) => { if (e.target === e.currentTarget) dialog.current?.close(); }}
      >
        <img src={value.path} alt={value.alt ?? ''} onClick={() => dialog.current?.close()} />
        <button type="button" className="enlarge-close" onClick={() => dialog.current?.close()}>
          <span aria-hidden="true">✕</span>
        </button>
      </dialog>
    </>
  );
}
